import { useTranslation } from "react-i18next";

import { Box, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";

import { EllipsisText } from "../../../../ellipsis-text/EllipsisText";
import { formatEuro } from "../../../../format/formatNumber";
import { SurchargeRow } from "./BalanceSurchargeDialog.types";

interface Props {
  rows: SurchargeRow[];
  /** While false only the current balance is shown per row — there is nothing to project yet. */
  hasPreview: boolean;
}

export function BalanceSurchargeRows({ rows, hasPreview }: Props) {
  const { t, i18n } = useTranslation();
  const euro = (amount: number) => formatEuro(amount, i18n.language);

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{t("claims.surcharge.creditor")}</TableCell>
          <TableCell align="right">{t("claims.surcharge.balance")}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <ClaimRow key={row.claim.id} row={row} hasPreview={hasPreview} euro={euro} />
        ))}
      </TableBody>
    </Table>
  );
}

interface RowProps {
  row: SurchargeRow;
  hasPreview: boolean;
  euro: (amount: number) => string;
}

function ClaimRow({ row, hasPreview, euro }: RowProps) {
  const { t } = useTranslation();
  const isSkipped = row.skipReason !== null;
  const color = isSkipped ? "text.disabled" : undefined;

  return (
    <TableRow>
      {/* maxWidth: 0 lets this column shrink below its text, so a long name truncates instead of widening the dialog. */}
      <TableCell sx={{ width: "100%", maxWidth: 0 }}>
        <EllipsisText value={row.claim.creditorName} variant="body2" fontWeight={600} color={color} />
      </TableCell>
      <TableCell align="right" sx={{ color, whiteSpace: "nowrap" }}>
        {isSkipped ? (
          t("claims.surcharge.skippedNoHistory")
        ) : (
          <>
            <Box component="span" sx={{ color: hasPreview ? "text.secondary" : undefined }}>
              {euro(row.claim.currentSaldo)}
            </Box>
            {hasPreview && (
              <>
                {" → "}
                {/* A balance the surcharge leaves where it is reads as repeated, not raised. */}
                <Box component="span" fontWeight={row.keepsSaldo ? undefined : 600}>
                  {euro(row.newSaldo)}
                </Box>
              </>
            )}
          </>
        )}
      </TableCell>
    </TableRow>
  );
}
